// Unified page header — single source of truth for all list/data-view page titles.
//
// Every page renders the same h1 size, subtitle placement, action slot, and back
// link pattern. Pages choose their own children for the actions area so view
// toggle, create button, grid columns, and refresh button keep their layout.
//
// Warm Precision (spec D `.pagehead`): h1 17px/600 with tight tracking, 12px
// tertiary subtitle, 8px action gaps, 4px/16px vertical rhythm — mirrors the
// dashboard reference header (DashboardPage).

import { type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';

// List surfaces opt into the square row checkbox (see list.css).
import './list.css';

export interface PageHeaderProps {
  /** Page title (already translated or raw). */
  title: string;
  /** Descriptive subtitle or entity count below the title. */
  subtitle?: ReactNode;
  /** Action buttons row on the right side (create, view toggle, grid columns, refresh). */
  actions?: ReactNode;
  /** Optional back-link path — renders an arrow + label above the title. */
  backTo?: string;
  /** Label for the back link (already translated). */
  backLabel?: string;
  /** Additional class names forwarded to the root <header>. */
  className?: string;
  /** data-testid on the root <header>. */
  testId?: string;
  /** Optional extra content below the header row (e.g. filter bar). */
  children?: ReactNode;
}

/**
 * Renders a standardised page header.
 *
 * Layout (top → bottom):
 *   [back link]          (optional)
 *   [title  .......... actions]
 *   [subtitle]          (optional)
 *   [children]          (extra — FilterBar, stats row)
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  backTo,
  backLabel,
  className = '',
  testId,
  children,
}: PageHeaderProps): ReactElement {
  return (
    <header
      data-testid={testId}
      className={`z-10 flex w-full shrink-0 flex-col ${className}`}
      style={{ margin: '4px 0 16px' }}
    >
      {backTo ? (
        <Link
          to={backTo}
          className="mb-2 inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase hover:text-[var(--ant-color-text)]"
          style={{ color: 'var(--ant-color-text-secondary)' }}
        >
          <ArrowLeftOutlined />
          {backLabel}
        </Link>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 basis-40">
          <h1
            className="m-0 leading-tight"
            style={{
              fontSize: 17,
              fontWeight: 600,
              letterSpacing: '-0.015em',
              color: 'var(--ant-color-text-heading)',
            }}
          >
            {title}
          </h1>
          {subtitle ? (
            <p
              className="m-0"
              style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', marginTop: 2 }}
            >
              {subtitle}
            </p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>

      {children ? <div className="mt-3">{children}</div> : null}
    </header>
  );
}

export default PageHeader;
